import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Divider, 
  Chip,
  Grid
} from '@mui/material';
import {
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Share as ShareIcon,
  ContactSupport as ContactIcon,
  Policy as PolicyIcon
} from '@mui/icons-material';
import Navigation from '../components/Navigation';

const Privacy: React.FC = () => {
  const sections = [
    { id: 'collection', title: 'Information We Collect', icon: <DataUsageIcon /> },
    { id: 'usage', title: 'How We Use Your Information', icon: <SecurityIcon /> },
    { id: 'sharing', title: 'Information Sharing', icon: <ShareIcon /> },
    { id: 'integration', title: 'Data Integration and Processing', icon: <DataUsageIcon /> },
    { id: 'security', title: 'Security', icon: <SecurityIcon /> },
    { id: 'changes', title: 'Changes to This Policy', icon: <PolicyIcon /> },
    { id: 'contact', title: 'Contact Us', icon: <ContactIcon /> }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{ 
        color: 'white', 
        py: 8,
        background: 'linear-gradient(135deg, #4caf50 0%, #6fbf73 30%, #357a38 60%, #4caf50 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />
        
        {/* Navigation */}
        <Navigation />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Privacy Policy
            </Typography>
            
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', lineHeight: 1.4 }}>
              Your privacy matters - here's how we protect it
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Alerts Section */}
      <Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
        <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body2" component="div">
            <strong>Last Updated:</strong> January 2025 | <strong>Status:</strong> In Effect
          </Typography>
        </Alert>
      </Container>
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Table of Contents */}
        <Paper sx={{ p: 6, mb: 6, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
            Privacy Policy Overview
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4, color: 'text.secondary' }}>
            This Privacy Policy describes how FamilyCast collects, uses, and protects your information when you use our family digital signage platform and related services.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sections.map((section) => (
              <Chip
                key={section.id}
                label={section.title}
                variant="outlined"
                size="small"
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ 
                  cursor: 'pointer',
                  borderColor: '#8B4513',
                  color: '#4caf50',
                  '&:hover': { 
                    bgcolor: '#4caf50', 
                    color: 'white',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>

        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            {/* Main Content */}
            <Paper sx={{ p: 6, borderRadius: 2, boxShadow: 2 }}>
              {/* Section 1: Information We Collect */}
              <Box id="collection" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                  Information We Collect
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
                  To provide our digital signage services effectively, we collect various types of information:
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
                  Personal Information
                </Typography>
                <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 4 }}>
                  <li>Name, email address, and contact information</li>
                  <li>Account credentials and authentication data</li>
                  <li>Billing and payment information</li>
                  <li>Organization details and user preferences</li>
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
                  Usage Data
                </Typography>
                <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
                  <li>Display activity and content performance metrics</li>
                  <li>System logs and technical diagnostics</li>
                  <li>Feature usage and interaction patterns</li>
                  <li>Device information and browser details</li>
                </Typography>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Section 2: How We Use Your Information */}
              <Box id="usage" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                  How We Use Your Information
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                  We use your information to deliver, improve, and secure our services:
                </Typography>
                <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
                  <li><strong>Service Delivery:</strong> Provide and maintain your digital signage displays</li>
                  <li><strong>Account Management:</strong> Process billing, manage subscriptions, and provide support</li>
                  <li><strong>Communication:</strong> Send service notifications, updates, and support responses</li>
                  <li><strong>Improvement:</strong> Analyze usage patterns to enhance features and performance</li>
                  <li><strong>Security:</strong> Detect threats, prevent abuse, and maintain system integrity</li>
                  <li><strong>Legal Compliance:</strong> Meet regulatory requirements and enforce our terms</li>
                </Typography>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Section 3: Information Sharing */}
              <Box id="sharing" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                  Information Sharing
                </Typography>
                <Alert severity="info" sx={{ mb: 4, borderColor: '#8B4513', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#4caf50' }}>
                    <strong>We do not sell your personal information.</strong> We only share your information in limited circumstances as described below.
                  </Typography>
                </Alert>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
                  Service Providers
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
                  We share information with trusted third-party service providers:
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Alert severity="info" variant="outlined" sx={{ mb: 2, borderColor: '#8B4513' }}>
                    <Typography variant="body1" sx={{ color: '#4caf50' }}>
                      <strong>Payment Processing:</strong> Stripe processes billing information securely
                    </Typography>
                  </Alert>
                  <Alert severity="info" variant="outlined" sx={{ mb: 2, borderColor: '#8B4513' }}>
                    <Typography variant="body1" sx={{ color: '#4caf50' }}>
                      <strong>Content Integrations:</strong> Google Photos and Canva for authorized content access
                    </Typography>
                  </Alert>
                  <Alert severity="info" variant="outlined" sx={{ borderColor: '#8B4513' }}>
                    <Typography variant="body1" sx={{ color: '#4caf50' }}>
                      <strong>Infrastructure:</strong> Cloud hosting and CDN providers for service delivery
                    </Typography>
                  </Alert>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
                  Legal Requirements
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.0rem', lineHeight: 1.6 }}>
                  We may disclose information when required by law, to protect our rights, or to ensure user safety.
                </Typography>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Section 4: Data Integration */}
              <Box id="integration" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                  Data Integration and Processing
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                  FamilyCast integrates with various external services to power your family displays:
                </Typography>
                <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 4 }}>
                  <li><strong>Calendar Services:</strong> Display upcoming events and schedules</li>
                  <li><strong>Content Management:</strong> Pull images and designs from your connected accounts</li>
                  <li><strong>Weather APIs:</strong> Show current conditions and forecasts</li>
                  <li><strong>Inventory Systems:</strong> Display real-time product information</li>
                </Typography>
                <Alert severity="warning" sx={{ borderColor: '#D2691E', bgcolor: 'rgba(210, 105, 30, 0.05)' }}>
                  <Typography variant="body1" sx={{ color: '#4caf50' }}>
                    <strong>Important:</strong> We only process data necessary for display purposes and do not store or share integrated content beyond what's required for service delivery.
                  </Typography>
                </Alert>
              </Box>

              <Divider sx={{ my: 6 }} />

              {/* Section 5: Security */}
              <Box id="security" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                  Security
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                  We implement industry-standard security measures to protect your information:
                </Typography>
                <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
                  <li><strong>Encryption:</strong> Data encrypted in transit and at rest using TLS/SSL</li>
                  <li><strong>Access Control:</strong> Role-based permissions and authentication</li>
                  <li><strong>Monitoring:</strong> Continuous security monitoring and threat detection</li>
                  <li><strong>Regular Updates:</strong> Security patches and system updates</li>
                  <li><strong>Data Backup:</strong> Regular backups with secure storage</li>
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            {/* California Privacy Rights */}
            <Paper sx={{ p: 4, boxShadow: 2, borderRadius: 2, height: 'fit-content', mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
                California Privacy Rights
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.0rem', lineHeight: 1.6, mb: 3 }}>
                Under the California Consumer Privacy Act (CCPA), California residents have specific rights:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 1.4 }}>
                <li>Right to know what information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of information sales</li>
                <li>Right to non-discrimination</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Contact <strong>privacy@familycast.app</strong> to exercise these rights.
              </Typography>
            </Paper>

            {/* Quick Contact */}
            <Paper sx={{ p: 4, boxShadow: 2, borderRadius: 2, height: 'fit-content' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
                Privacy Questions?
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.0rem', lineHeight: 1.6, mb: 3 }}>
                Our privacy team is here to help with any questions or concerns about your data.
              </Typography>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#8B4513', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  <strong>Privacy Team:</strong><br />
                  privacy@familycast.app
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>

        {/* Bottom Sections */}
        <Paper sx={{ p: 6, mt: 6, borderRadius: 2, boxShadow: 2 }}>
          {/* Changes to Policy */}
          <Box id="changes" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. When we make material changes:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
              <li>We'll notify you via email or service notification</li>
              <li>The updated policy will be posted on this page</li>
              <li>Continued use of our services constitutes acceptance</li>
              <li>You may terminate your account if you disagree with changes</li>
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Contact Information */}
          <Box id="contact" sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#8B4513', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Privacy Inquiries:</strong><br />privacy@brandcast.app
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#8B4513', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>General Support:</strong><br />support@familycast.app
                </Typography>
              </Alert>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Privacy;